import { createContext, type PropsWithChildren, useContext } from 'react';
import type { Nullable } from '@gov-nx/core/types';
import type { ServiceItem, PPPSeznamContextTypes } from './service.types';
import { PPPSubRoutes, type ServiceCode, usePPPLocale } from '../..';
import { serviceSubRouteResolver } from '@gov-nx/web';
import { usePPPSeznamQuery } from './hooks/usePPPSeznamQuery';
import { usePPPSeznamFilter } from './hooks/usePPPSeznamFilter';
import { usePPPSeznamTabs } from './hooks/usePPPSeznamTabs';
import { useSearchForm } from './formDefinition';
import { useBoolean } from '@gov-nx/utils/common';
import { useRezaSwitcherStore } from '@gov-nx/store';

export const PPPSeznamContext =
	createContext<Nullable<PPPSeznamContextTypes>>(null);

export const PPPSeznamContextProvider = ({
	children,
	code,
}: PropsWithChildren<{ code: ServiceCode }>) => {
	const { t } = usePPPLocale();
	const isModalFormOpen = useBoolean();
	const items: Array<ServiceItem> = [
		{
			title: t('seznam.sluzby.zapis.nadpis'),
			description: t('seznam.sluzby.zapis.popis'),
			link: serviceSubRouteResolver('PPP', PPPSubRoutes.ZAPIS),
			icon: { name: 'chevron-right', type: 'components' },
		},
	];

	const {
		filter,
		filterParams,
		goToPage,
		currentPage,
		pageSize,
		hasFilterChanges,
		onFilterParamsReset,
	} = usePPPSeznamFilter();
	const tabs = usePPPSeznamTabs();
	const { activeSwitcherReza, setActiveSwitcherReza } = useRezaSwitcherStore();
	const query = usePPPSeznamQuery(filter, activeSwitcherReza?.kod);
	const searchForm = useSearchForm();

	const pppData = query.data?.seznam || [];

	return (
		<PPPSeznamContext.Provider
			value={{
				code,
				isModalFormOpen,
				items,
				complaintLink: serviceSubRouteResolver('PPP', PPPSubRoutes.REKLAMACE),
				tabs,
				pppData,
				isDataEmpty: pppData.length === 0,
				numberOfDataItems: query.data?.pocet || 0,
				isLoading: query.isLoading,
				isError: query.isError,
				searchForm,
				goToPage,
				currentPage,
				pageSize,
				hasFilterChanges: hasFilterChanges(),
				onFilterParamsReset,
				filterParams,
				activeSwitcherReza,
				setActiveSwitcherReza,
			}}>
			{children}
		</PPPSeznamContext.Provider>
	);
};

export const usePPPSeznamContext = (): PPPSeznamContextTypes =>
	useContext(PPPSeznamContext) as PPPSeznamContextTypes;
